/** SDRL import — inspect, map, preview, commit. In that order, never collapsed.
 *
 * Real supplier document requirements lists have a title block above the grid, merged
 * cells and vendor-specific column names. So the file is inspected first (header row and a
 * proposed mapping), the mapping is yours to correct, the preview shows exactly which rows
 * would be created and which would be skipped and why, and only then does anything commit.
 * An SDRL misparsed into 40 wrong obligations is worse than no register at all.
 */
import { useRef, useState } from 'react';
import { CheckCircle2, FileUp, Loader2, Upload } from 'lucide-react';
import api from '@/lib/api';
import { useNfaTheme } from '@/components/nfa/nfaTheme';
import { Btn, Empty, Field, Input, Panel, Select, Stat } from '@/components/nfa/NfaPrimitives';

const STEPS = ['Choose file', 'Map columns', 'Preview', 'Committed'];

export const EdmSdrlImport = ({ projectId, onImported }) => {
  const T = useNfaTheme();
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [step, setStep] = useState(0);
  const [po, setPo] = useState('');
  const [vendorKey, setVendorKey] = useState('');
  const [insp, setInsp] = useState(null);
  const [mapping, setMapping] = useState({});
  const [headerRow, setHeaderRow] = useState(0);
  const [prev, setPrev] = useState(null);
  const [done, setDone] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const inspect = async (f) => {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      if (vendorKey) fd.append('vendor_key', vendorKey);
      const r = await api.post(`/edm/projects/${projectId}/vdr/sdrl/inspect`, fd);
      setInsp(r.data); setMapping(r.data.mapping || {});
      setHeaderRow(r.data.headerRowIndex); setFile(f); setStep(1);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not read that file');
    } finally { setBusy(false); }
  };

  const preview = async () => {
    setBusy(true); setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('po_number', po);
      fd.append('header_row', String(headerRow));
      fd.append('mapping', JSON.stringify(mapping));
      const r = await api.post(`/edm/projects/${projectId}/vdr/sdrl/preview`, fd);
      setPrev(r.data); setStep(2);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Preview failed');
    } finally { setBusy(false); }
  };

  const commit = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.post(`/edm/projects/${projectId}/vdr/sdrl/commit`, {
        po_number: po, filename: file.name, vendor_key: vendorKey || null,
        mapping, rows: prev.rows });
      setDone(r.data); setStep(3);
      onImported?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Commit failed');
    } finally { setBusy(false); }
  };

  const reset = () => {
    setFile(null); setInsp(null); setPrev(null); setDone(null); setStep(0); setErr(null);
  };

  return (
    <div className="space-y-3" data-testid="edm-sdrl-import">
      <div className="flex items-center gap-1.5 flex-wrap">
        {STEPS.map((s, i) => (
          <span key={s} data-testid={`edm-sdrl-step-${i}`}
            className="px-2.5 py-1 rounded-md text-[10.5px] font-semibold"
            style={i === step
              ? { background: T.primary, color: '#fff' }
              : { background: T.surfaceAlt, color: T.muted, border: `1px solid ${T.border}` }}>
            {i + 1}. {s}
          </span>
        ))}
      </div>

      {err && <div className="rounded-lg px-3 py-2 text-[11px]" data-testid="edm-sdrl-error"
        style={{ background: T.tint(T.red), color: T.red,
          border: `1px solid ${T.tint(T.red, '44')}` }}>{err}</div>}

      {step === 0 && (
        <Panel T={T} title="Supplier document requirements list"
          subtitle="CSV or XLSX. Nothing is imported until you have seen the preview."
          testId="edm-sdrl-choose">
          <div className="grid grid-cols-2 gap-3">
            <Field T={T} label="Purchase order" hint="Every obligation hangs off a PO.">
              <Input T={T} value={po} onChange={(e) => setPo(e.target.value)}
                data-testid="edm-sdrl-po" placeholder="PO-TEST-9001" /></Field>
            <Field T={T} label="Vendor key (optional)"
              hint="Saves the column mapping so the next file from this vendor needs no work.">
              <Input T={T} value={vendorKey} onChange={(e) => setVendorKey(e.target.value)}
                data-testid="edm-sdrl-vendor" placeholder="isgec" /></Field>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden"
            data-testid="edm-sdrl-file"
            onChange={(e) => e.target.files?.[0] && inspect(e.target.files[0])} />
          <div className="mt-4">
            <Btn T={T} onClick={() => fileRef.current?.click()} disabled={!po || busy}
              testId="edm-sdrl-choose-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileUp className="w-3.5 h-3.5" />}
              Choose SDRL file
            </Btn>
            {!po && <span className="ml-2 text-[10.5px]" style={{ color: T.muted }}>
              Enter the PO number first.</span>}
          </div>
        </Panel>
      )}

      {step === 1 && insp && (
        <Panel T={T} title={`Map the columns — ${insp.filename}`}
          subtitle={insp.note} testId="edm-sdrl-mapping"
          right={<>
            <span className="text-[10px] px-2 py-0.5 rounded-full"
              data-testid="edm-sdrl-mapping-source"
              style={{ background: T.tint(T.primary), color: T.primary }}>
              {insp.mappingSource}
            </span>
            <Btn T={T} variant="ghost" onClick={reset}>Start again</Btn>
            <Btn T={T} onClick={preview} disabled={busy} testId="edm-sdrl-preview-btn">
              {busy && <Loader2 className="w-3 h-3 animate-spin" />} Preview rows</Btn>
          </>}>
          <div className="grid grid-cols-2 gap-3">
            <Field T={T} label="Header row"
              hint={`Row ${insp.headerRowIndex + 1} looked most like column names.`}>
              <Input T={T} type="number" value={headerRow} data-testid="edm-sdrl-header-row"
                onChange={(e) => setHeaderRow(Number(e.target.value))} /></Field>
          </div>
          <div className="grid grid-cols-2 gap-3 mt-3">
            {Object.entries(insp.targets).map(([key, spec]) => (
              <Field T={T} key={key}
                label={`${spec.label}${spec.required ? ' *' : ''}`}>
                <Select T={T} value={mapping[key] ?? ''} data-testid={`edm-sdrl-map-${key}`}
                  onChange={(e) => setMapping({ ...mapping,
                    [key]: e.target.value === '' ? null : Number(e.target.value) })}>
                  <option value="">— not in this file —</option>
                  {insp.headers.map((h, i) => (
                    <option key={i} value={i}>{i}: {h || '(blank)'}</option>))}
                </Select>
              </Field>
            ))}
          </div>
          <div className="mt-4 overflow-x-auto">
            <div className="text-[9.5px] uppercase tracking-[0.08em] mb-1"
              style={{ color: T.muted }}>First rows as read</div>
            <table className="text-[10.5px]">
              <tbody>
                {insp.sample.map((row, i) => (
                  <tr key={i}>{row.map((c, j) => (
                    <td key={j} className="px-2 py-1 whitespace-nowrap"
                      style={{ color: T.body, borderBottom: `1px solid ${T.border}` }}>{c}</td>
                  ))}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}

      {step === 2 && prev && (
        <>
          <div className="grid grid-cols-3 gap-2">
            <Stat T={T} label="Rows read" value={prev.count} testId="edm-sdrl-rows"
              info="Data rows found in the sheet after the header row you mapped. Blank and
                    heading rows are not counted." />
            <Stat T={T} label="Will import" value={prev.importable} tone={T.green}
              testId="edm-sdrl-importable"
              info="Rows with everything an obligation needs: a PO reference, a document type
                    and a title. These are the only rows the commit will create." />
            <Stat T={T} label="Will be skipped" value={prev.withProblems}
              tone={prev.withProblems ? T.amber : undefined} sub="skipped, never guessed at"
              testId="edm-sdrl-skipped"
              info="Rows with a stated problem. They are skipped and listed with the reason —
                    a missing document type is never filled in with a plausible guess,
                    because a wrong obligation is worse than a missing one." />
          </div>
          <Panel T={T} title="Preview" subtitle={prev.note} testId="edm-sdrl-preview" pad={false}
            right={<>
              <Btn T={T} variant="ghost" onClick={() => setStep(1)}>Back to mapping</Btn>
              <Btn T={T} onClick={commit} disabled={busy || !prev.importable}
                testId="edm-sdrl-commit-btn">
                {busy && <Loader2 className="w-3 h-3 animate-spin" />}
                Import {prev.importable} obligation(s)</Btn>
            </>}>
            <div className="grid grid-cols-12 px-4 py-2 text-[9.5px] uppercase tracking-[0.08em]"
              style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`,
                color: T.muted }}>
              <div className="col-span-1">Row</div><div className="col-span-1">Line</div>
              <div className="col-span-2">Type</div><div className="col-span-3">Title</div>
              <div className="col-span-1">Vendor no.</div>
              <div className="col-span-1">Planned</div><div className="col-span-1">Required</div>
              <div className="col-span-2">Problem</div>
            </div>
            {prev.rows.map((r) => (
              <div key={r.sourceRow} data-testid={`edm-sdrl-row-${r.sourceRow}`}
                className="grid grid-cols-12 px-4 py-2 text-[11px] items-center"
                style={{ borderBottom: `1px solid ${T.border}`,
                  background: r.issues.length ? T.tint(T.amber, '0E') : 'transparent' }}>
                <div className="col-span-1 tabular-nums" style={{ color: T.muted }}>{r.sourceRow}</div>
                <div className="col-span-1 font-mono text-[10px]" style={{ color: T.body }}>{r.po_line}</div>
                <div className="col-span-2 font-mono text-[10px]" style={{ color: T.ink }}>
                  {r.document_type_code || '—'}</div>
                <div className="col-span-3 truncate pr-2" style={{ color: T.ink }}>{r.title || '—'}</div>
                <div className="col-span-1 font-mono text-[10px]" style={{ color: T.body }}>
                  {r.vendor_document_number || '—'}</div>
                <div className="col-span-1 tabular-nums" style={{ color: T.body }}>
                  {r.planned_submission || '—'}</div>
                <div className="col-span-1 tabular-nums" style={{ color: T.body }}>
                  {r.required_by || '—'}</div>
                <div className="col-span-2 text-[9.5px]" style={{ color: T.amber }}>
                  {r.issues.join('; ')}</div>
              </div>
            ))}
            {prev.count === 0 && <Empty T={T}>No data rows below that header row.</Empty>}
          </Panel>
        </>
      )}

      {step === 3 && done && (
        <Panel T={T} title="Imported" testId="edm-sdrl-done"
          right={<Btn T={T} variant="ghost" onClick={reset}>Import another</Btn>}>
          <div className="flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 mt-0.5" style={{ color: T.green }} />
            <div>
              <p className="text-[12px]" style={{ color: T.ink }}>
                {done.created} obligation(s) created, {done.skipped} skipped,
                {' '}{done.failed} failed.
              </p>
              <p className="text-[10.5px] mt-1" style={{ color: T.muted }}>{done.note}</p>
              {(done.skippedDetail || []).length > 0 && (
                <ul className="mt-2 text-[10px] space-y-0.5" style={{ color: T.amber }}>
                  {done.skippedDetail.map((s) => (
                    <li key={s.sourceRow}>row {s.sourceRow}: {(s.why || []).join('; ')}</li>))}
                </ul>
              )}
            </div>
          </div>
        </Panel>
      )}
    </div>
  );
};

export default EdmSdrlImport;
